// pages/vendors/soft-skill/View.tsx
import DetailView from "@/components/DetailView";
import { useSoftSkillVendorByIdQuery } from "@/hooks/api/softSkillVendor.queries";
import { useParams } from "react-router-dom";
import { Spinner, ErrorMessage } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Mail, MapPin } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const SoftSkillVendorView = () => {
  const { id } = useParams<{ id: string }>();
  const { data: vendor, isLoading, isError, refetch } = useSoftSkillVendorByIdQuery(id || "");

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (isError || !vendor) return <ErrorMessage message="Failed to load soft skill vendor details." onRetry={() => refetch()} />;

  return (
    <DetailView
      title="Soft Skill Vendor"
      backPath="/vendors/soft-skill"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs">Edit Vendor</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs">Send Message</Button>
        </div>
      }
    >
      {/* Profile Card */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="pb-8 pt-8">
          <div className="flex flex-col gap-6 md:flex-row">
            <Avatar className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-xl font-bold text-white">
              <AvatarFallback className="bg-blue-600 text-white">
                {vendor.vendorName.split(" ").map((n: string) => n[0]).join("").toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900">{vendor.vendorName}</h2>
                <Badge variant={vendor.status === "active" ? "secondary" : "destructive"} className="rounded-full px-3 font-medium capitalize">{vendor.status}</Badge>
                {vendor.expertise.map((exp: string) => (
                  <Badge key={exp} variant="outline" className="rounded-full px-3 font-medium">{exp}</Badge>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-x-12 gap-y-2 text-sm text-gray-500 md:grid-cols-2">
                <span><Mail size={13} className="mr-1 inline" />{vendor.email}</span>
                <span>📞 {vendor.phone}</span>
                <span><MapPin size={13} className="mr-1 inline" />{vendor.city}, {vendor.state}</span>
                <span><Star size={13} className="mr-1 inline fill-yellow-500 text-yellow-500" />{vendor.rating} / 5.0</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Person */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="pt-6">
          <p className="mb-4 text-sm font-semibold text-gray-700">Contact Person</p>
          <div className="flex w-fit min-w-[300px] items-center gap-4 rounded-xl border border-gray-100 bg-gray-50/30 p-4">
            <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
              <AvatarFallback className="bg-blue-100 font-bold text-blue-600">
                {vendor.contactPerson.split(" ").map((n: string) => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-bold text-gray-900">{vendor.contactPerson}</p>
              <p className="mt-1 flex items-center gap-1 text-xs text-blue-600"><Mail size={12} />{vendor.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </DetailView>
  );
};

export default SoftSkillVendorView;
